import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Plus, 
  Upload, 
  Music, 
  Trash2, 
  Edit, 
  Play, 
  LogOut, 
  Crown,
  FileText,
  User,
  Headphones,
  X,
  Download,
  AlertCircle,
  Copy,
  RefreshCw
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import api, { BACKEND_BASE_URL } from '../lib/api';

// Lever name patterns - these names indicate knee levers
const LEVER_PATTERNS = ['LKL', 'LKR', 'RKL', 'RKR', 'LKV', 'RKV', 'LK', 'RK'];

// Helper to detect if a control name is a lever based on naming patterns
const isLeverName = (name) => {
  const upperName = (name || '').toUpperCase().trim();
  return LEVER_PATTERNS.some(pattern => upperName.startsWith(pattern));
};

// Helper to count pedals and levers
const countControlTypes = (controls) => {
  let pedals = 0;
  let levers = 0;
  
  (controls || []).forEach(ctrl => {
    if (ctrl.control_type === 'lever') {
      levers++;
    } else if (ctrl.control_type === 'pedal') {
      pedals++;
    } else {
      if (isLeverName(ctrl.name)) {
        levers++;
      } else {
        pedals++;
      }
    }
  });
  
  return { pedals, levers };
};

// Sample CSV template
const CSV_TEMPLATE = `string_number,open_note,P1,P2,LKL
1,F#,0,0,0
2,D,0,0,+1
3,B,+2,0,0
4,G#,0,+1,0
5,E,0,0,0
6,B,+2,0,0
7,G#,0,+1,0
8,E,0,0,0
9,D,0,0,+1
10,B,0,0,0`;

// CSV Validation
const validateCSV = (content) => {
  const errors = [];
  const lines = content.trim().split('\n');
  
  if (lines.length < 2) {
    errors.push('CSV must have at least a header row and one data row');
    return { valid: false, errors };
  }
  
  const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
  if (!headers.includes('string_number') || !headers.includes('open_note')) {
    errors.push('Missing required columns: string_number, open_note');
  }
  
  const validNotes = ['c', 'c#', 'db', 'd', 'd#', 'eb', 'e', 'f', 'f#', 'gb', 'g', 'g#', 'ab', 'a', 'a#', 'bb', 'b'];
  const noteIndex = headers.indexOf('open_note');
  
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim());
    if (noteIndex >= 0 && values[noteIndex]) {
      const note = values[noteIndex].toLowerCase().replace(/[0-9]/g, '');
      if (!validNotes.includes(note)) {
        errors.push(`Row ${i + 1}: Invalid note "${values[noteIndex]}"`);
      }
    }
  }
  
  return { valid: errors.length === 0, errors };
};

export const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Copedant data state - separate user and sample copedants
  const [userCopedants, setUserCopedants] = useState([]);
  const [sampleCopedants, setSampleCopedants] = useState([]);
  
  // Loading and error states - separate for user and samples
  const [loadingUser, setLoadingUser] = useState(true);
  const [loadingSamples, setLoadingSamples] = useState(true);
  const [errorUser, setErrorUser] = useState(null);
  const [errorSamples, setErrorSamples] = useState(null);
  
  const [deleteDialog, setDeleteDialog] = useState({ open: false, copedant: null });
  
  // Import modal state
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importName, setImportName] = useState('');
  const [csvContent, setCsvContent] = useState('');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importMode, setImportMode] = useState('file'); // 'file' or 'paste'
  const fileInputRef = useRef(null);

  // Fetch all copedants - memoized to avoid recreation
  const fetchCopedants = useCallback(async () => {
    setLoadingUser(true);
    setLoadingSamples(true);
    setErrorUser(null);
    setErrorSamples(null);
    
    try {
      const response = await api.get('/copedants');
      const allCopedants = response.data || [];
      
      // Separate user and sample copedants
      setUserCopedants(allCopedants.filter(c => !c.is_sample));
      setSampleCopedants(allCopedants.filter(c => c.is_sample));
    } catch (error) {
      console.error('Error fetching copedants:', error);
      
      // Handle 401 - redirect to login
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        navigate('/login');
        return;
      }
      
      // Set errors for both sections
      const errorMsg = error.response?.data?.detail || 'Failed to load copedants';
      setErrorUser(errorMsg);
      setErrorSamples(errorMsg);
    } finally {
      setLoadingUser(false);
      setLoadingSamples(false);
    }
  }, [navigate]);

  // Fetch data on mount AND when navigating back to this route
  useEffect(() => {
    fetchCopedants();
  }, [fetchCopedants, location.key]); // location.key changes on each navigation

  const handleDelete = async (copedant) => {
    try {
      await api.delete(`/copedants/${copedant.copedant_id}`);
      // Update local state immediately for better UX
      setUserCopedants(prev => prev.filter(c => c.copedant_id !== copedant.copedant_id));
      setDeleteDialog({ open: false, copedant: null });
      toast.success('Copedant deleted');
    } catch (error) {
      console.error('Error deleting copedant:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        navigate('/login');
        return;
      }
      const message = error.response?.data?.detail || 'Failed to delete copedant';
      toast.error(message);
    }
  };

  const handleCreateNew = () => {
    navigate('/copedants/new');
  };

  const handleExplore = (copedantId) => {
    navigate(`/explorer?copedantId=${copedantId}`);
  };

  const handleEdit = (copedantId) => {
    navigate(`/copedants/${copedantId}/edit`);
  };

  // Import handlers
  const openImportModal = () => {
    setImportModalOpen(true);
    setImportName('');
    setCsvContent('');
    setImportError(null);
    setImportMode('file');
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const fileName = file.name.toLowerCase();
    const isPDF = fileName.endsWith('.pdf') || file.type === 'application/pdf';
    const isCSV = fileName.endsWith('.csv') || file.type === 'text/csv';
    
    if (!isPDF && !isCSV) {
      toast.error('Please select a CSV or PDF file');
      return;
    }
    
    setImportName(file.name.replace(/\.(csv|pdf)$/i, ''));
    setImportError(null);
    
    if (isPDF) {
      // Handle PDF - navigate to PDF import page with file
      setImportModalOpen(false);
      navigate('/copedants/import-pdf');
      return;
    }
    
    // Handle CSV
    try {
      const text = await file.text();
      setCsvContent(text);
      setImportMode('paste');
      
      // Validate
      const validation = validateCSV(text);
      if (!validation.valid) {
        setImportError(validation.errors.join('. '));
      }
    } catch (error) {
      toast.error('Failed to read file');
    }
  };

  const handleImport = async () => {
    if (!csvContent.trim()) {
      toast.error('Please provide CSV content');
      return;
    }
    
    // Validate before import
    const validation = validateCSV(csvContent);
    if (!validation.valid) {
      setImportError(validation.errors.join('. '));
      return;
    }

    setImporting(true);
    setImportError(null);
    
    try {
      const response = await api.post(
        '/copedants/import-csv',
        { csv_content: csvContent, name: importName || 'Imported Copedant' }
      );
      toast.success(`Imported: ${importName || 'Imported Copedant'}`);
      setImportModalOpen(false);
      // Update local state with the new copedant
      if (response.data) {
        setUserCopedants(prev => [...prev, response.data]);
      } else {
        // Fallback to full refresh
        await fetchCopedants();
      }
    } catch (error) {
      console.error('Error importing CSV:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        navigate('/login');
        return;
      }
      const message = error.response?.data?.detail || 'We couldn\'t read that file—try using the CSV template format.';
      if (message.startsWith('UPGRADE_REQUIRED:')) {
        toast.error(message.replace('UPGRADE_REQUIRED:', ''));
        setImportModalOpen(false);
        navigate('/upgrade');
        return;
      }
      setImportError(message);
    } finally {
      setImporting(false);
    }
  };

  const copyTemplate = () => {
    navigator.clipboard.writeText(CSV_TEMPLATE);
    toast.success('CSV template copied to clipboard');
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'copedant_template.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  // Combined loading state for initial page load
  const isInitialLoading = loadingUser && loadingSamples && userCopedants.length === 0 && sampleCopedants.length === 0;

  return (
    <div className="min-h-screen bg-ss-bg">
      {/* Noise overlay */}
      <div className="noise-overlay"></div>

      {/* Header */}
      <header className="sticky top-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-ss-surface border border-ss-cyan/30 flex items-center justify-center">
              <Music className="w-5 h-5 text-ss-cyan" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-bold tracking-tight uppercase text-ss-text">
                Sacred Steel Explorer
              </h1>
              <p className="text-xs text-ss-muted tracking-widest uppercase">by StreakLabs</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {user && (
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/account')}
                  className="text-ss-muted hover:text-ss-text"
                  data-testid="account-btn"
                >
                  <User className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">{user.name}</span>
                </Button>
                {user.picture && (
                  <img 
                    src={user.picture} 
                    alt={user.name}
                    className="w-8 h-8 rounded-full border border-ss-border"
                  />
                )}
              </div>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={logout}
              data-testid="logout-btn"
              className="text-ss-muted hover:text-ss-text"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Action Buttons - Clean row */}
        <div className="flex flex-wrap items-center gap-3 mb-8">
          <Button
            onClick={handleCreateNew}
            data-testid="create-copedant-btn"
            className="bg-ss-cyan hover:bg-ss-cyan/90 text-black font-semibold"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create New
          </Button>
          <Button
            onClick={openImportModal}
            data-testid="import-btn"
            variant="outline"
            className="border-ss-border hover:border-ss-cyan/50 text-ss-text"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import Copedant
          </Button>
          <Button
            onClick={() => navigate('/shed-tracks')}
            data-testid="shed-tracks-btn"
            variant="outline"
            className="border-ss-purple/50 hover:border-ss-purple text-ss-purple"
          >
            <Headphones className="w-4 h-4 mr-2" />
            Shed Tracks
          </Button>
          <div className="flex-1" />
          <Button
            onClick={() => navigate('/upgrade')}
            data-testid="upgrade-btn"
            className="bg-gradient-to-r from-ss-gold/20 to-ss-gold/10 hover:from-ss-gold/30 hover:to-ss-gold/20 border border-ss-gold/50 text-ss-gold"
          >
            <Crown className="w-4 h-4 mr-2" />
            {user?.is_pro ? 'Pro' : 'Upgrade'}
          </Button>
        </div>

        {isInitialLoading ? (
          <div className="flex justify-center py-20">
            <div className="w-12 h-12 border-4 border-ss-cyan border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {/* User's Copedants */}
            <section className="mb-10">
              <h2 className="font-heading text-xl font-bold tracking-tight text-ss-text mb-5 flex items-center gap-2">
                <span className="w-1 h-5 bg-ss-cyan rounded-full"></span>
                Your Copedants
                <span className="ml-2 text-sm font-normal text-ss-muted">
                  ({userCopedants.length}{!user?.is_pro && '/1 free'})
                </span>
              </h2>

              {/* Error state for user copedants */}
              {errorUser ? (
                <Card className="bg-ss-surface border-ss-border border-ss-red/30">
                  <CardContent className="py-8 text-center">
                    <AlertCircle className="w-10 h-10 text-ss-red mx-auto mb-3" />
                    <p className="text-ss-text mb-2">Couldn't load your copedants</p>
                    <p className="text-sm text-ss-muted mb-4">{errorUser}</p>
                    <Button
                      onClick={fetchCopedants}
                      variant="outline"
                      className="border-ss-cyan text-ss-cyan hover:bg-ss-cyan/10"
                      data-testid="retry-user-copedants-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Retry
                    </Button>
                  </CardContent>
                </Card>
              ) : loadingUser ? (
                /* Skeleton loading state */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {[1, 2, 3].map((i) => (
                    <Card key={i} className="bg-ss-surface border-ss-border animate-pulse">
                      <CardHeader className="pb-2">
                        <div className="h-5 bg-ss-border rounded w-3/4 mb-2"></div>
                        <div className="h-4 bg-ss-border/50 rounded w-1/2"></div>
                      </CardHeader>
                      <CardContent>
                        <div className="mb-4 p-2.5 bg-ss-bg rounded-lg border border-ss-border">
                          <div className="h-3 bg-ss-border/50 rounded w-1/4 mb-2"></div>
                          <div className="flex gap-1">
                            {[1,2,3,4,5].map(j => (
                              <div key={j} className="w-7 h-7 bg-ss-border rounded"></div>
                            ))}
                          </div>
                        </div>
                        <div className="h-9 bg-ss-border rounded"></div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : userCopedants.length === 0 ? (
                <Card className="bg-ss-surface border-ss-border">
                  <CardContent className="py-10 text-center">
                    <Music className="w-10 h-10 text-ss-muted mx-auto mb-3" />
                    <p className="text-ss-text mb-1">No copedants yet</p>
                    <p className="text-sm text-ss-muted mb-4">Create a copedant or import one to start exploring chords.</p>
                    <div className="flex justify-center gap-3">
                      <Button
                        onClick={handleCreateNew}
                        className="bg-ss-cyan hover:bg-ss-cyan/90 text-black"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Create
                      </Button>
                      <Button
                        onClick={openImportModal}
                        variant="outline"
                        className="border-ss-border text-ss-text"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Import
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {userCopedants.map(copedant => (
                    <CopedantCard
                      key={copedant.copedant_id}
                      copedant={copedant}
                      onEdit={() => handleEdit(copedant.copedant_id)}
                      onExplore={() => handleExplore(copedant.copedant_id)}
                      onDelete={() => setDeleteDialog({ open: true, copedant })}
                    />
                  ))}
                </div>
              )}
            </section>

            {/* Sample Copedants */}
            <section>
              <h2 className="font-heading text-xl font-bold tracking-tight text-ss-text mb-5 flex items-center gap-2">
                <span className="w-1 h-5 bg-ss-red rounded-full"></span>
                Sample Copedants
              </h2>

              {/* Error state for sample copedants */}
              {errorSamples ? (
                <Card className="bg-ss-surface border-ss-border border-ss-red/30">
                  <CardContent className="py-8 text-center">
                    <AlertCircle className="w-10 h-10 text-ss-red mx-auto mb-3" />
                    <p className="text-ss-text mb-2">Couldn't load sample copedants</p>
                    <p className="text-sm text-ss-muted mb-4">{errorSamples}</p>
                    <Button
                      onClick={fetchCopedants}
                      variant="outline"
                      className="border-ss-cyan text-ss-cyan hover:bg-ss-cyan/10"
                      data-testid="retry-sample-copedants-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Retry
                    </Button>
                  </CardContent>
                </Card>
              ) : loadingSamples ? (
                /* Skeleton loading state for samples */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {[1, 2, 3, 4].map((i) => (
                    <Card key={i} className="bg-ss-surface border-ss-border animate-pulse">
                      <CardHeader className="pb-2">
                        <div className="h-5 bg-ss-border rounded w-3/4 mb-2"></div>
                        <div className="h-4 bg-ss-border/50 rounded w-1/2"></div>
                      </CardHeader>
                      <CardContent>
                        <div className="mb-4 p-2.5 bg-ss-bg rounded-lg border border-ss-border">
                          <div className="h-3 bg-ss-border/50 rounded w-1/4 mb-2"></div>
                          <div className="flex gap-1">
                            {[1,2,3,4,5].map(j => (
                              <div key={j} className="w-7 h-7 bg-ss-border rounded"></div>
                            ))}
                          </div>
                        </div>
                        <div className="h-9 bg-ss-border rounded"></div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : sampleCopedants.length === 0 ? (
                <Card className="bg-ss-surface border-ss-border">
                  <CardContent className="py-8 text-center">
                    <Music className="w-10 h-10 text-ss-muted mx-auto mb-3" />
                    <p className="text-ss-text mb-1">No sample copedants available</p>
                    <p className="text-sm text-ss-muted">Sample copedants will appear here when available.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {sampleCopedants.map(copedant => (
                    <CopedantCard
                      key={copedant.copedant_id}
                      copedant={copedant}
                      isSample
                      onExplore={() => handleExplore(copedant.copedant_id)}
                    />
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>

      {/* Import Modal */}
      <Dialog open={importModalOpen} onOpenChange={setImportModalOpen}>
        <DialogContent className="bg-ss-surface border-ss-border text-ss-text max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Upload className="w-5 h-5 text-ss-cyan" />
              Import Copedant
            </DialogTitle>
            <DialogDescription className="text-ss-muted text-sm">
              Upload a CSV or PDF file, or paste CSV data directly.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 py-2">
            {/* Name field */}
            <div>
              <Label className="text-ss-muted text-sm">Copedant Name</Label>
              <Input
                value={importName}
                onChange={(e) => setImportName(e.target.value)}
                placeholder="My Custom Copedant"
                className="mt-1.5 bg-ss-bg border-ss-border text-ss-text"
                data-testid="import-name-input"
              />
            </div>

            {/* File Upload Zone */}
            <div>
              <Label className="text-ss-muted text-sm">Upload File</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.pdf,text/csv,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="mt-1.5 border-2 border-dashed border-ss-border hover:border-ss-cyan/50 rounded-lg p-6 text-center cursor-pointer transition-colors"
              >
                <Upload className="w-8 h-8 text-ss-muted mx-auto mb-2" />
                <p className="text-sm text-ss-text font-medium">Click to upload or drag & drop</p>
                <p className="text-xs text-ss-muted mt-1">
                  Supports: <span className="text-ss-cyan">CSV</span> or <span className="text-ss-muted">PDF</span>
                </p>
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-ss-border"></div>
              <span className="text-xs text-ss-muted uppercase tracking-wider">or</span>
              <div className="flex-1 h-px bg-ss-border"></div>
            </div>

            {/* Paste CSV (Advanced) */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-ss-muted text-sm">Paste CSV (Advanced)</Label>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={copyTemplate}
                    className="h-7 px-2 text-xs text-ss-cyan hover:text-ss-cyan/80"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copy Template
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={downloadTemplate}
                    className="h-7 px-2 text-xs text-ss-muted hover:text-ss-text"
                  >
                    <Download className="w-3 h-3 mr-1" />
                    Download
                  </Button>
                </div>
              </div>
              <textarea
                value={csvContent}
                onChange={(e) => {
                  setCsvContent(e.target.value);
                  setImportError(null);
                }}
                placeholder="string_number,open_note,P1,P2,LKL&#10;1,F#,0,0,0&#10;2,D,0,0,+1&#10;..."
                className="w-full h-32 px-3 py-2 bg-ss-bg border border-ss-border rounded-md text-ss-text font-mono text-xs resize-none focus:outline-none focus:ring-1 focus:ring-ss-cyan"
                data-testid="csv-content-input"
              />
              <p className="text-[11px] text-ss-muted mt-1">
                Expected columns: string_number, open_note, then control names (P1, P2, LKL, etc.)
              </p>
            </div>

            {/* Error display */}
            {importError && (
              <div className="p-3 bg-ss-red/10 border border-ss-red/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-ss-red flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-ss-red">{importError}</p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setImportModalOpen(false)}
              className="border-ss-border text-ss-text"
            >
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={importing || !csvContent.trim()}
              data-testid="confirm-import-btn"
              className="bg-ss-cyan hover:bg-ss-cyan/90 text-black font-semibold"
            >
              {importing ? (
                <>
                  <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Import
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, copedant: null })}>
        <DialogContent className="bg-ss-surface border-ss-border text-ss-text">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Delete Copedant</DialogTitle>
            <DialogDescription className="text-ss-muted">
              Are you sure you want to delete "{deleteDialog.copedant?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, copedant: null })}
              className="border-ss-border text-ss-text"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleDelete(deleteDialog.copedant)}
              data-testid="confirm-delete-btn"
              className="bg-ss-red hover:bg-ss-red/90 text-white"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const CopedantCard = ({ copedant, isSample, onEdit, onExplore, onDelete }) => {
  const { pedals, levers } = countControlTypes(copedant.controls);
  const isLapSteel = copedant.instrument_type === 'lap_steel' || (!copedant.controls || copedant.controls.length === 0);
  
  // Build the control type label
  let controlText;
  if (isLapSteel) {
    controlText = 'Lap Steel';
  } else {
    const controlLabel = [];
    if (pedals > 0) controlLabel.push(`${pedals} pedal${pedals !== 1 ? 's' : ''}`);
    if (levers > 0) controlLabel.push(`${levers} knee lever${levers !== 1 ? 's' : ''}`);
    controlText = controlLabel.join(' • ') || 'No controls';
  }

  return (
    <Card 
      className="bg-ss-surface border-ss-border hover:border-ss-cyan/30 transition-all duration-200 group"
      data-testid={`copedant-card-${copedant.copedant_id}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="font-heading text-[1.1rem] font-semibold text-ss-text flex items-center gap-2 mb-1">
              {copedant.name}
              {isSample && (
                <span className="text-[10px] px-2 py-0.5 bg-ss-red/20 text-ss-red rounded-full uppercase tracking-wider">
                  Sample
                </span>
              )}
            </CardTitle>
            <CardDescription className="text-ss-muted text-sm">
              {copedant.strings?.length || 0} strings • {controlText}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* String preview - Open Tuning Pills */}
        <div className="mb-4 p-2.5 bg-ss-bg rounded-lg border border-ss-border">
          <p className="text-[10px] text-ss-muted mb-1.5 uppercase tracking-wider">Open Tuning</p>
          <div className="flex flex-wrap gap-1">
            {copedant.strings?.slice(0, 12).map((s, idx) => (
              <span 
                key={idx}
                className="w-7 h-7 flex items-center justify-center bg-ss-surface-highlight border border-ss-border rounded text-xs font-mono text-ss-cyan"
              >
                {s.open_note}
              </span>
            ))}
            {copedant.strings?.length > 12 && (
              <span className="w-7 h-7 flex items-center justify-center text-ss-muted text-xs">
                +{copedant.strings.length - 12}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            onClick={onExplore}
            data-testid={`explore-${copedant.copedant_id}`}
            className="flex-1 bg-ss-cyan hover:bg-ss-cyan/90 text-black font-semibold"
          >
            <Play className="w-4 h-4 mr-2" />
            Explore
          </Button>
          {!isSample && (
            <>
              <Button
                onClick={onEdit}
                variant="outline"
                className="border-ss-border text-ss-muted hover:text-ss-text"
                data-testid={`edit-${copedant.copedant_id}`}
              >
                <Edit className="w-4 h-4" />
              </Button>
              <Button
                onClick={onDelete}
                variant="outline"
                className="border-ss-border text-ss-red hover:bg-ss-red/10"
                data-testid={`delete-${copedant.copedant_id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default DashboardPage;
